import { useMemo } from "react";
import { Link } from "react-router";
import { ArrowRight, CalendarClock, Circle, FileText } from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  Subtitle,
  VBadge,
  VButton,
  cn,
} from "@/components/ui";
import { useCurrentUser } from "@/domains/auth";
import { useCases } from "@/domains/cases";
import { CaseStatusBadge } from "@/domains/cases";
import type { CaseFile } from "@/domains/cases";
import { useRequestsByRequester, RequestStatusBadge } from "@/domains/requests";
import type { CameraRequest } from "@/domains/requests";
import { useTasks } from "@/domains/tasks/api/queries";
import type { CameraTask } from "@/domains/tasks/api/types";

const TODAY = "2026-05-11";

type RequestWithCase = {
  request: CameraRequest;
  caseFile?: CaseFile;
  tasks: ReadonlyArray<CameraTask>;
};

export function FollowUpPage(): React.JSX.Element {
  const currentUser = useCurrentUser();
  const requestsQuery = useRequestsByRequester(currentUser.id);
  const casesQuery = useCases();
  const tasksQuery = useTasks();

  const items = useMemo<ReadonlyArray<RequestWithCase>>(() => {
    const requests = requestsQuery.data ?? [];
    const cases = casesQuery.data ?? [];
    const allTasks = tasksQuery.data ?? [];
    return requests.map((request) => {
      const caseFile = cases.find((c) => c.id === request.caseId);
      const tasks = caseFile ? allTasks.filter((t) => t.caseId === caseFile.id) : [];
      return { request, caseFile, tasks };
    });
  }, [requestsQuery.data, casesQuery.data, tasksQuery.data]);

  if (requestsQuery.isPending || casesQuery.isPending || tasksQuery.isPending) {
    return <LoadingState label="Opvolging laden…" />;
  }
  if (requestsQuery.isError || casesQuery.isError || tasksQuery.isError) {
    return <ErrorMessage message="Kon opvolging niet ophalen." />;
  }

  if (items.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Opvolging"
          subtitle="Volg de status van je aanvragen en geplande activiteiten."
        />
        <EmptyState
          title="Geen aanvragen om op te volgen"
          description="Eens je een aanvraag hebt ingediend zie je hier de geplande activiteiten."
          action={
            <VButton asChild>
              <Link to="/requests/new">Nieuwe aanvraag</Link>
            </VButton>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Opvolging"
        subtitle="Tijdslijn van je aanvragen en geplande activiteiten. Read-only — bij vragen contacteer je behandelaar."
      />

      <ul className="space-y-4">
        {items.map((item) => (
          <FollowUpItem key={item.request.id} item={item} />
        ))}
      </ul>
    </div>
  );
}

type FollowUpItemProps = {
  item: RequestWithCase;
};

function FollowUpItem({ item }: FollowUpItemProps): React.JSX.Element {
  const { request, caseFile, tasks } = item;
  const sortedTasks = useMemo<ReadonlyArray<CameraTask>>(() => {
    return [...tasks].sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  }, [tasks]);

  return (
    <li>
      <Card padding="lg" className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CardTitle>{request.title}</CardTitle>
            <Caption tone="muted">
              Aanvraag {request.number} · ingediend op {request.submittedAt.slice(0, 10)}
            </Caption>
            {caseFile ? (
              <Caption tone="muted">
                Dossier #{caseFile.number} · {caseFile.startDate} → {caseFile.endDate} ·{" "}
                {caseFile.address}
              </Caption>
            ) : (
              <Caption tone="muted">
                Wacht op behandeling — er is nog geen dossier aangemaakt.
              </Caption>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <RequestStatusBadge status={request.status} />
            {caseFile ? <CaseStatusBadge status={caseFile.status} /> : null}
            <VButton variant="outline" size="sm" asChild>
              <Link to={`/requests/${request.id}`}>
                <FileText className="size-4" aria-hidden />
                Aanvraag
              </Link>
            </VButton>
          </div>
        </div>

        {sortedTasks.length > 0 ? (
          <FollowUpTimeline tasks={sortedTasks} />
        ) : (
          <Subtitle>
            {caseFile
              ? "Nog geen geplande activiteiten op het dossier."
              : "Zodra je aanvraag is goedgekeurd zie je hier de geplande activiteiten."}
          </Subtitle>
        )}
      </Card>
    </li>
  );
}

type FollowUpTimelineProps = {
  tasks: ReadonlyArray<CameraTask>;
};

function FollowUpTimeline({ tasks }: FollowUpTimelineProps): React.JSX.Element {
  return (
    <ol className="space-y-2">
      {tasks.map((task) => {
        const isDone = task.status === "done";
        const isToday = task.dueDate === TODAY;
        const isPast = task.dueDate < TODAY;
        const upcomingTone = isDone
          ? "text-success-700"
          : isToday
            ? "text-primary-700"
            : isPast
              ? "text-warning-700"
              : "text-muted-foreground";
        return (
          <li key={task.id} className="flex items-start gap-3">
            <div className="flex shrink-0 flex-col items-center pt-1">
              {isDone ? (
                <span
                  className="inline-flex size-3 items-center justify-center rounded-full bg-success-600"
                  aria-hidden
                />
              ) : isToday ? (
                <span
                  className="inline-flex size-3 animate-pulse rounded-full bg-primary-600"
                  aria-hidden
                />
              ) : (
                <Circle className={cn("size-3", upcomingTone)} aria-hidden />
              )}
            </div>
            <div className="min-w-0 flex-1 rounded-md border border-border bg-muted/30 px-3 py-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <Body weight={isDone ? "normal" : "semibold"}>{task.title}</Body>
                <Caption tone="muted" className="inline-flex items-center gap-1">
                  <CalendarClock className="size-3" aria-hidden />
                  {task.dueDate}
                </Caption>
              </div>
              {task.description ? (
                <Caption tone="muted" className="mt-0.5">
                  {task.description}
                </Caption>
              ) : null}
              {isDone ? (
                <VBadge color="success" className="mt-1">
                  Afgerond
                </VBadge>
              ) : isToday ? (
                <VBadge color="primary" className="mt-1">
                  Vandaag gepland
                </VBadge>
              ) : isPast ? (
                <VBadge color="warning" className="mt-1">
                  Wacht op afronding
                </VBadge>
              ) : null}
            </div>
            <ArrowRight className="mt-3 hidden size-4 shrink-0 text-muted-foreground md:block" aria-hidden />
          </li>
        );
      })}
    </ol>
  );
}
